import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import SM from 'services/ServiceManager';
import Service from 'additiv-services';
import ServerError from 'errors/ServerError';
import AdapterError from 'errors/AdapterError';
import { useFormatting } from 'locale';
import { adaptModelPortfolio } from '../adapters/adaptModelPortfolio';

// --- Types ---

interface ModelPortfolioState {
    data: any; // The adapted data
    dataRaw: any; // The raw API response
    error: any;
    isLoading: boolean;
}

interface AdaptOptions {
    currencyId?: string | number;
    [key: string]: any; // For additional options passed to the adapter
}

interface ModelPortfolioOptions {
    modelPortfolioId?: number | string;
    adaptOptions?: AdaptOptions;
    adaptData?: (args: any) => any;
    loadInitially?: boolean;
}

const initialState: ModelPortfolioState = {
    data: {},
    dataRaw: {},
    error: null,
    isLoading: false,
};

// --- Hook ---

export const useModelPortfolio = (options: ModelPortfolioOptions = {}) => {
    const {
        modelPortfolioId: modelPortfolioIdOption,
        adaptOptions: adaptOptionsOption = undefined,
        adaptData = adaptModelPortfolio,
        loadInitially = false,
    } = options;

    const [state, setState] = useState<ModelPortfolioState>(initialState);

    const queryClient = useQueryClient();
    const { t, i18n: { language } } = useTranslation();
    const { getFormattedDate, getFormattedNumber, getFormattedCurrency } = useFormatting();

    const getModelPortfolio = useCallback(async (
        modelPortfolioId: number | string | undefined = modelPortfolioIdOption,
        { adaptOptions }: { adaptOptions?: AdaptOptions } = { adaptOptions: adaptOptionsOption },
    ) => {
        if (!modelPortfolioId) {
            setState((prev) => ({ ...prev, error: null, isLoading: false }));

            return null;
        }

        setState((prev) => ({ ...prev, error: null, isLoading: true }));

        const params: Record<string, any> = { language };

        if (adaptOptions?.currencyId) {
            params.currencyId = adaptOptions.currencyId;
        }

        try {
            const response = await queryClient.fetchQuery({
                queryKey: ['getModelPortfolio', modelPortfolioId, params],
                queryFn: () => SM.portfolioManagement('getModelPortfolio', [modelPortfolioId, params]),
                staleTime: 5 * 60 * 1000,
                gcTime: 10 * 60 * 1000,
            }) as any;

            try {
                const adapted = adaptData({
                    data: response.data,
                    t,
                    getFormattedDate,
                    getFormattedNumber,
                    getFormattedCurrency,
                    ...adaptOptions,
                });

                setState({
                    data: adapted, dataRaw: response.data, error: null, isLoading: false,
                });

                return response.data;
            } catch (err: any) {
                throw new AdapterError(err);
            }
        } catch (err: any) {
            if (Service.isCanceled(err)) {
                setState((prev) => ({ ...prev, isLoading: false }));

                return err;
            }

            const wrapped = err?.type !== undefined ? err : new ServerError(err);

            setState((prev) => ({ ...prev, isLoading: false, error: wrapped }));

            return wrapped;
        }
    }, [
        adaptData,
        adaptOptionsOption,
        modelPortfolioIdOption,
        queryClient,
        getFormattedCurrency,
        getFormattedDate,
        getFormattedNumber,
        language,
        t,
    ]);

    // Effects
    useEffect(() => {
        if (loadInitially) {
            getModelPortfolio();
        }
    }, [getModelPortfolio, loadInitially]);

    return {
        data: state.data,
        dataRaw: state.dataRaw,
        error: state.error,
        isLoading: state.isLoading,
        getModelPortfolio,
    };
};
